"use client";

import { useScriptStore } from "@/hooks/use-script";
import { createScriptSchema } from "@/lib/validations/script";
import { zodResolver } from "@hookform/resolvers/zod";
import type * as z from "zod";
import { useForm, Controller } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { InputTags } from "@/components/script/input-tags";
import { Combobox } from "@/components/ui/combo-box";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import Link from "next/link";
import { cn } from "@/lib/utils";
import {
  BadgeCheck,
  Ellipsis,
  FileCode,
  MailWarning,
  ThumbsDown,
  ThumbsUp,
  Upload,
  X,
} from "lucide-react";
import { useTransition, useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { env } from "@/env";
import { useRouter } from "next/navigation";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import type { APIResponse } from "@/types/script";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import Image from "next/image";
import { useAuth } from "@/context/AuthContext";
import { buildAvatarLink } from "@/lib/image";
import LoadingSpinner from "@/components/ui/loading-spinner";
import CollapsibleDescription from "./collapsible-description";
import MDEditor from "@uiw/react-md-editor";
import rehypeSanitize from "rehype-sanitize";
import { GameSelection } from "@/components/game/game-selection";

interface Game {
  universeId: number;
  name: string;
  thumbnail: string;
  creator: string;
  playerCount?: number;
}

const MAX_FILES = 3;
const MAX_FILE_SIZE = 1024 * 1024 * 2;

export default function CreateScriptForm() {
  const { user, accessToken } = useAuth();
  const { script, updateScript } = useScriptStore();
  const { toast } = useToast();
  const [isPending, startTransition] = useTransition();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const router = useRouter();

  const form = useForm<z.infer<typeof createScriptSchema>>({
    resolver: zodResolver(createScriptSchema),
    defaultValues: {
      title: "",
      description: "",
      images: [],
      tags: script.tags,
      games: [] as Game[],
      source: "",
      mobile: false,
      key_system: false,
      payment: "Free",
    },
  });

  const payment = form.watch("payment");
  const isFree = payment === "Free";

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const currentCount = form.getValues("images").length;
      const remainingSlots = MAX_FILES - currentCount;

      if (remainingSlots <= 0) {
        toast({
          title: "Error",
          description: `Maximum ${MAX_FILES} images allowed`,
          variant: "destructive",
        });
        return;
      }

      const validFiles = acceptedFiles
        .slice(0, remainingSlots)
        .filter((file) => {
          if (file.size > MAX_FILE_SIZE) {
            toast({
              title: "Error",
              description: `${file.name} is too large. Max size is 2MB`,
              variant: "destructive",
            });
            return false;
          }
          return true;
        });

      validFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          form.setValue("images", [...form.getValues("images"), base64]);
        };
        reader.readAsDataURL(file);
      });
    },
    [form, toast],
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/*": [".jpg", ".jpeg", ".png", ".gif", ".svg", ".webp", ".avif"],
    },
    maxFiles: MAX_FILES,
    maxSize: MAX_FILE_SIZE,
    onDrop,
    disabled: form.getValues("images").length >= MAX_FILES,
  });

  async function onSubmit(data: z.infer<typeof createScriptSchema>) {
    setIsSubmitting(true);
    startTransition(async () => {
      try {
        const body: any = {
          title: data.title,
          description: data.description,
          images: data.images,
          tags: script.tags,
          source: data.source,
          mobile: data.mobile,
          key_system: data.key_system,
          payment: data.payment,
        };

        if (data.games && data.games.length > 0) {
          body.games = data.games.map((game) => game.universeId.toString());
        }

        const response = await fetch(`${env.NEXT_PUBLIC_API_URL}/post`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(body),
        });

        const responseData: APIResponse = await response.json();

        if (responseData.message !== "OK") {
          if (responseData.errors) {
            Object.keys(responseData.errors).forEach((key) => {
              const error = responseData.errors![key];
              toast({
                title: "Error",
                description: `${error!.param.path}: ${error!.param.msg}`,
                variant: "destructive",
              });
            });
          } else {
            toast({
              title: "Error",
              description: responseData.message,
              variant: "destructive",
            });
          }
        } else {
          toast({
            title: "Success",
            description: "Script created successfully",
          });
          router.push(`/script/${responseData.data?.post.slug}`);
        }
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      } finally {
        setIsSubmitting(false);
      }
    });
  }

  const handleImageDelete = useCallback(
    (index: number) => {
      const currentImages = form.getValues("images");
      const newImages = [...currentImages];
      newImages.splice(index, 1);
      form.setValue("images", newImages);
    },
    [form],
  );

  return (
    <main>
      {isSubmitting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4">
            <LoadingSpinner className="h-16 w-16" />
            <p>Hold on, this may take a minute or more...</p>
          </div>
        </div>
      )}
      <div className="mx-auto mt-4 max-w-7xl gap-8 px-6 py-20 lg:px-8 lg:py-32">
        {!user!.verified && (
          <Alert className="mb-8" variant="warning">
            <MailWarning className="h-4 w-4" />
            <AlertTitle>Email Verification Required</AlertTitle>
            <AlertDescription>
              You need to verify your email address before you can create a
              script.{" "}
              <Link
                href="/user/settings/account"
                className="text-primary hover:underline"
              >
                Verify your email.
              </Link>
            </AlertDescription>
          </Alert>
        )}

        <h1 className="font-heading text-2xl font-medium">
          Create a new script
        </h1>
        <p className="text-base text-muted-foreground">
          Fill out the form below to create a new script
        </p>

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit, (errors) => {
                  console.log("Form validation errors:", errors);
                })}
                className="space-y-8"
              >
                <div className="space-y-2">
                  <h2 className="mb-3 font-medium">Basic Information</h2>
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Script Title*</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter title of the script"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem className="mt-2">
                        <FormLabel>Description*</FormLabel>
                        <FormControl>
                          <div className="w-full">
                            <MDEditor
                              value={field.value}
                              onChange={field.onChange}
                              textareaProps={{
                                maxLength: 20000,
                              }}
                              preview="edit"
                              previewOptions={{
                                rehypePlugins: [[rehypeSanitize]],
                              }}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="images"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Images*</FormLabel>
                        <FormControl>
                          <div>
                            <div
                              {...getRootProps({
                                className:
                                  "hover:bg-white/5 transition hover:cursor-pointer gap-2 flex items-center justify-center flex-col border-dashed border border-input py-8 px-4 rounded-md",
                              })}
                            >
                              <input {...getInputProps()} />
                              <div className="rounded-full bg-white/5 p-4">
                                <Upload
                                  className="h-4 w-4 text-muted-foreground"
                                  aria-hidden="true"
                                />
                              </div>
                              <span className="flex flex-col gap-px text-center">
                                <p>
                                  Drag 'n' drop some files here, or click to
                                  select files
                                </p>
                                <span className="text-xs text-muted-foreground">
                                  You can upload 3 images (up to 2MB each)
                                </span>
                              </span>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {field.value.map((image, index) => (
                                <div key={index} className="group relative">
                                  <img
                                    src={image}
                                    alt={`Uploaded image ${index + 1}`}
                                    className="h-20 w-20 rounded-md object-cover"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleImageDelete(index)}
                                    className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-destructive-foreground opacity-0 transition-opacity group-hover:opacity-100"
                                  >
                                    <X className="h-4 w-4" />
                                    <span className="sr-only">
                                      Remove image
                                    </span>
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="games"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Games</FormLabel>
                        <FormControl>
                          <GameSelection
                            selectedGames={field.value || []}
                            onChange={(games) => {
                              field.onChange(games);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="source"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Code*</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder='loadstring(game:HttpGet("https://raw.githubusercontent.com/EdgeIY/infiniteyield/master/source",true))()'
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <hr className="border-input" />

                <div className="space-y-2">
                  <h2 className="mb-3 font-medium">Additional Information</h2>
                  <Controller
                    name="tags"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tags*</FormLabel>
                        <InputTags
                          value={script.tags ?? []}
                          onChange={(tags) =>
                            updateScript({
                              tags: tags as string[],
                            })
                          }
                          placeholder="Enter values, comma separated..."
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="mobile"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border border-input p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">
                            Mobile Compatible
                          </FormLabel>
                          <FormDescription>
                            Is your script mobile compatible?
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="key_system"
                    render={({ field }) => (
                      <FormItem
                        className={cn(
                          "flex flex-row items-center justify-between rounded-lg border border-input p-4",
                          !isFree && "opacity-50",
                        )}
                      >
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">
                            Key System
                          </FormLabel>
                          <FormDescription>
                            Does your script have a key system?
                            {!isFree && " (Only available for free scripts)"}
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            disabled={!isFree}
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="payment"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border border-input p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">Payment</FormLabel>
                          <FormDescription>
                            Is your script free or paid?
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Combobox
                            {...field}
                            options={[
                              { value: "Free", label: "Free" },
                              { value: "Paid", label: "Paid" },
                            ]}
                            selected={field.value}
                            placeholder="Select option..."
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Button type="submit" disabled={isPending}>
                  {isPending ? "Submitting..." : "Submit"}
                </Button>
              </form>
            </Form>
          </div>

          <div className="sticky">
            <h2 className="mb-3 text-xl font-semibold">Preview</h2>
            <div className="space-y-4">
              <header>
                <AspectRatio
                  ratio={16 / 9}
                  className="overflow-hidden rounded-md border border-white/5"
                >
                  {form.watch("images").map((image, index) => (
                    <Image
                      key={index}
                      src={image}
                      alt={`Uploaded image ${index + 1}`}
                      fill
                      objectFit="cover"
                    />
                  ))}
                </AspectRatio>

                <div className="mt-4 flex w-full flex-col gap-2">
                  <h2 className="font-heading text-pretty text-xl font-semibold tracking-tight">
                    {form.watch("title")}
                  </h2>

                  <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
                    {/* Author */}
                    <div className="flex flex-row items-start gap-2">
                      <Avatar>
                        <AvatarImage
                          src={buildAvatarLink(user!.username!)}
                          alt="Author avatar"
                        />
                        <AvatarFallback>{user?.username[0]}</AvatarFallback>
                      </Avatar>
                      <span className="flex flex-col break-words text-sm text-muted-foreground">
                        <span className="flex flex-row items-center gap-0.5 text-foreground">
                          <Link
                            href={`/user/${user!.username}`}
                            className="hover:underline"
                          >
                            @{user!.username}
                          </Link>
                          {user!.verified && (
                            <BadgeCheck className="size-4 fill-primary text-card" />
                          )}
                        </span>
                        <span>69 views • 1 hour ago</span>
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-row items-center gap-1.5">
                      <Button>
                        <FileCode size={16} /> View script
                      </Button>

                      <span className="flex flex-row items-center rounded-md border border-input text-muted-foreground">
                        <button className="btn inline-flex h-9 gap-1.5 rounded-l-md bg-background px-4 py-2 shadow-sm hover:bg-accent hover:text-accent-foreground">
                          <ThumbsUp size={16} /> 1
                        </button>
                        <hr className="h-9 border-r border-input" />
                        <button className="btn inline-flex h-9 gap-1.5 rounded-r-md bg-background px-4 py-2 shadow-sm hover:bg-accent hover:text-accent-foreground">
                          <ThumbsDown size={16} /> 0
                        </button>
                      </span>

                      <Button variant="outline" size="icon" className="px-2">
                        <Ellipsis size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              </header>

              {/* Description */}
              <CollapsibleDescription
                description={form.watch("description")}
                tags={script.tags ?? []}
              />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
